import '@vaadin/radio-group';
// eslint-disable-next-line no-duplicate-imports
import type {
    RadioGroupValueChangedEvent,
    RadioGroup as VaadinRadioGroup,
} from '@vaadin/radio-group';
import { css, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { TmInputFieldBase } from './tm-input-field.base';
import { type RadioGroupOption, type RadioGroupTheme } from './tm-radio-group.types';

export const tagName = 'tm-radio-group';
@customElement(tagName)
export class TmRadioGroup extends TmInputFieldBase<string> {
    @property({ type: Object, attribute: false })
    public options: RadioGroupOption[] = [];

    @property({ type: String, attribute: true, reflect: true })
    public radioGroupTheme: RadioGroupTheme = 'vertical';

    @property({ type: Boolean, attribute: true, reflect: true })
    public readOnly: boolean = false;

    @property({ type: String, attribute: false })
    public errorMessage?: string | null;

    private radioGroupRef = createRef<VaadinRadioGroup>();

    protected validate() {
        const radioGroup = this.radioGroupRef.value;

        if (!radioGroup) {
            return false;
        }

        return radioGroup.validate();
    }

    protected render() {
        return html`
            <vaadin-radio-group
                ${ref(this.radioGroupRef)}
                part="component"
                .label=${this.label}
                .value=${this.initial}
                .theme=${this.radioGroupTheme}
                ?disabled=${this.disabled}
                .readOnly=${this.readOnly}
                ?required=${this.required}
                .invalid=${this.invalid}
                ?errorMessage=${this.errorMessage}
                ?helperText=${this.helperText}
                @value-changed=${(e: RadioGroupValueChangedEvent) => {
                    this.changeValue(e.detail.value);
                }}
            >
                <slot>${this.renderRadioButtons()}</slot>
            </vaadin-radio-group>
        `;
    }

    private renderRadioButtons() {
        return this.options.map(
            (option: RadioGroupOption) =>
                html`<vaadin-radio-button
                    .checked=${this.value === option.value}
                    ?disabled=${option.disabled}
                    .label=${option.label}
                    .value=${option.value}
                >
                </vaadin-radio-button>`
        );
    }

    public static get styles() {
        return [
            ...super.styles,
            css`
                :host {
                    display: block;
                }

                *[has-label] {
                    margin-top: 10px;
                }

                *[has-label]::part(label) {
                    top: 10px;
                }

                *[has-label]::before {
                    height: 0px;
                }
                vaadin-radio-button {
                    font-family: var(--default-font-family);
                    --vaadin-radio-button-label-font-size: 14px;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmRadioGroup;
    }
}
